import { Link } from "react-router-dom";

// DRAFT — LEGAL REVIEW REQUIRED. Australian-oriented privacy notice
// for Subtend Studio beta. Must be reviewed by an Australian privacy
// specialist before general availability.
//
// LEGAL ENTITY DECISION REQUIRED: same as Terms — the trading name
// "Subtend Studio" is the product/service brand; the operating legal
// entity + privacy contact email domain remain pending.
const LEGAL_ENTITY = "[Legal Entity — LEGAL REVIEW REQUIRED]";
const PRIVACY_EMAIL = "privacy@luxemediawalls.com"; // pending domain migration

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
        <div className="overline mb-4">Legal · Draft</div>
        <h1 className="font-serif font-light text-4xl mb-4">Privacy Policy</h1>
        <p className="text-xs text-muted-foreground mb-8">
          Last updated: 14 February 2026 · Beta programme edition ·
          Draft for legal review.
        </p>

        <div className="border-l-2 border-[color:hsl(var(--accent))] pl-4 py-3 mb-10 bg-[color:hsl(var(--accent)/0.06)]">
          <div className="overline text-[10px] text-[color:hsl(var(--accent))] mb-1">Legal review required</div>
          <div className="text-xs text-muted-foreground leading-relaxed">
            The service is now presented as <strong>Subtend Studio</strong>. The
            legal contracting entity, ABN, registered business name and privacy
            contact email domain remain to be confirmed by the owner before
            general availability.
          </div>
        </div>

        <Section title="1. Who we are">
          {LEGAL_ENTITY} operates Subtend Studio, a design-to-proposal SaaS
          workspace. This Policy describes how we handle information when you
          use the Service.
        </Section>

        <Section title="2. Information we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li>Business account data (business name, contact email, phone, address).</li>
            <li>Client data your business enters (client name, contact details, project address).</li>
            <li>Uploaded photos and files (room photos, reference images).</li>
            <li>Project and design data (dimensions, materials, layouts).</li>
            <li>Email addresses of portal invitees.</li>
            <li>Authentication data (password hashes, session tokens).</li>
            <li>Product analytics and error logs.</li>
          </ul>
        </Section>

        <Section title="3. How we use it">
          To provide the Service, respond to support requests, secure the
          Service against abuse, generate proposals and other documents on
          your behalf, and improve the Service. We do not sell your data.
        </Section>

        <Section title="4. Third-party processors">
          The Service uses a small set of trusted subprocessors:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Emergent Platform — hosting, database, object storage.</li>
            <li>Resend — transactional email delivery.</li>
            <li>Emergent LLM Universal Key — AI-assisted text where enabled.</li>
          </ul>
          Each subprocessor is bound by its own privacy commitments.
        </Section>

        <Section title="5. Data location">
          Customer Data is stored on infrastructure operated by our hosting
          subprocessor. Some processing may occur outside Australia. Where
          this is the case, we take reasonable steps to protect the data.
        </Section>

        <Section title="6. Retention">
          Customer Data is retained while your account is active and for a
          reasonable period afterwards to allow reactivation and to satisfy
          legal obligations. Backups may retain data for up to 35 days after
          deletion from active systems.
        </Section>

        <Section title="7. Access, correction and deletion">
          You can access and correct most account data through the Service
          settings. To request a full export or deletion of your data, contact
          {" " + PRIVACY_EMAIL}. We will respond within 30 days.
        </Section>

        <Section title="8. Security">
          We use industry-standard measures including transport encryption
          (HTTPS), password hashing, tenant isolation and controlled access
          to production systems. No system is perfectly secure — we will
          notify affected customers of material incidents in accordance with
          Australian law.
        </Section>

        <Section title="9. Cookies and tracking">
          We use a small number of essential cookies for authentication and
          session management. We do not use third-party advertising cookies.
        </Section>

        <Section title="10. Children">
          The Service is intended for use by businesses. It is not directed
          to individuals under 16.
        </Section>

        <Section title="11. Updates">
          We may update this Policy. Material changes will be announced via
          the Service or by email to your primary account contact.
        </Section>

        <Section title="12. Contact">
          Questions or requests can be sent to {PRIVACY_EMAIL}. Email domain
          migration is pending owner decision — see the header notice above.
        </Section>

        <div className="mt-16 text-xs text-muted-foreground">
          <Link to="/terms" className="underline" data-testid="link-terms-from-privacy">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-xl mb-3">{title}</h2>
      <div className="text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
